/**
 * Avatars — object paths in the database, signed URLs only at read time.
 *
 * `profiles.avatar_url` holds a *storage object path* (`<user-id>/avatar-….jpg`)
 * for anything we host ourselves. It must never hold a signed URL: a signed URL
 * has an expiry baked into it, so the moment that expiry passes the avatar
 * silently 400s and the person's face disappears from their own profile.
 *
 * Third-party absolute URLs (Google, LinkedIn) are stored verbatim and passed
 * straight through — there is nothing for us to sign.
 */
import { supabase } from "@/integrations/supabase/client";

export const AVATAR_BUCKET = "avatars";

/**
 * Short-lived on purpose. A signed URL is generated per page load, so it only
 * has to outlive the render — not the account.
 */
export const AVATAR_SIGNED_URL_TTL_SECONDS = 60 * 60;

/** Re-signing on every render would be a request per avatar per paint. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const signedCache = new Map<string, { url: string; expiresAt: number }>();

/** An absolute URL we don't own, a blob: preview, or a data: URI. */
export function isExternalAvatarUrl(value: string): boolean {
  return /^(https?:|data:|blob:)/i.test(value.trim());
}

/** A value we should treat as a path inside the `avatars` bucket. */
export function isStoredAvatarPath(value: string | null | undefined): boolean {
  const v = (value ?? "").trim();
  if (!v) return false;
  return !isExternalAvatarUrl(v) && v.includes("/");
}

/** The canonical object path for a person's avatar. Owner-scoped first segment
 *  so the storage policy (`auth.uid() = foldername(name)[1]`) matches. */
export function avatarObjectPath(userId: string, extension = "jpg"): string {
  return `${userId}/avatar-${Date.now()}.${extension}`;
}

/**
 * Pull a storage path back out of a legacy signed URL, so a value written by
 * the old code path still renders instead of 404ing.
 * `…/storage/v1/object/sign/avatars/<path>?token=…` → `<path>`
 */
export function extractAvatarPath(value: string | null | undefined): string | null {
  const v = (value ?? "").trim();
  if (!v) return null;
  if (isStoredAvatarPath(v)) return v;
  const match = v.match(/\/storage\/v1\/object\/(?:sign|public|authenticated)\/avatars\/([^?#]+)/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function clearAvatarUrlCache(path?: string) {
  if (path) signedCache.delete(path);
  else signedCache.clear();
}

/**
 * Turn a stored `avatar_url` into something an `<img>` can use.
 *
 * Returns `null` when there is nothing renderable — including when the value is
 * a path the *current* viewer isn't allowed to sign (someone else's avatar).
 * Callers fall back to initials rather than showing a broken image.
 */
export async function resolveAvatarUrl(
  value: string | null | undefined,
): Promise<string | null> {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  if (isExternalAvatarUrl(raw)) return raw;

  const path = extractAvatarPath(raw);
  if (!path) return null;

  const cached = signedCache.get(path);
  if (cached && cached.expiresAt - REFRESH_MARGIN_MS > Date.now()) return cached.url;

  const { data, error } = await supabase.storage
    .from(AVATAR_BUCKET)
    .createSignedUrl(path, AVATAR_SIGNED_URL_TTL_SECONDS);

  if (error || !data?.signedUrl) return null;

  signedCache.set(path, {
    url: data.signedUrl,
    expiresAt: Date.now() + AVATAR_SIGNED_URL_TTL_SECONDS * 1000,
  });
  return data.signedUrl;
}
